/**
 * The pure half of the GIF picker (R3, R4): what query a given field state
 * searches for, how cursor pages accumulate into one grid, and how the
 * response envelope reads as available or not. Kept out of `GifPickerSheet`
 * so the paging and availability rules can be graded without rendering,
 * the same split `pollComposeLogic.ts` already made for the poll sheet.
 */
export type typeGifResult = {
  providerGifId: string;
  previewUrl: string;
  width: number;
  height: number;
};

/**
 * The 200 envelope `/gif/search` answers with, declared here rather than
 * imported: `packages/messaging-sdk/src/private/index.ts` exports the hook
 * but not its output type, and `mergeMessagePages.ts` already set the
 * convention of restating a wire shape locally in this feature.
 */
export type typeGifSearchEnvelope = {
  error?: { code: string; message: string } | null;
  data?: null | {
    items: typeGifResult[];
    nextCursor?: string | null;
    isProviderAvailable: boolean;
  };
};

/**
 * The seed the `Trending` shelf searches for before the fan types anything.
 *
 * `/gif/search` requires a query: `schemaGifSearchBody` sets
 * `minLength: 1` on it and `postGifSearchHandler` answers VALIDATION_ERROR
 * for a blank one, so an empty-string call is not an option. There is no
 * separate trending endpoint on messaging-api today, and adding one is a
 * schema change outside this card. Seeding the search with this word is the
 * honest way to fill the shelf: it is a real popularity query on the
 * provider, and it keeps the picker from opening on a blank field, which is
 * the whole point the brief gives for the shelf existing.
 */
export const GIF_TRENDING_QUERY = "trending";

/**
 * How long a `/gif/search` answer stays fresh, applied by BOTH the composer's
 * availability probe and the picker's own grid so they share one cache entry
 * rather than each paying the provider.
 *
 * This matters as a cost, not just a latency: the probe runs on every
 * conversation open, whether or not the fan ever taps the GIF action, and
 * `postGifSearchHandler` writes a `recordGifUsageEvent` for every call that
 * resolved a provider, "counted even when the provider answered
 * unavailable". `queryClient.ts`'s 30 second default would bill a search
 * roughly every half minute per client for a control most fans never touch.
 * Trending results do not turn over in five minutes, so this is a long
 * window with no visible staleness.
 *
 * Nothing is billed today: `resolveActiveGifProvider()` returns null with no
 * key, so no outbound call is made and no usage row is written. This exists
 * so the cost does not arrive unannounced with ISSUE-458's credential.
 */
export const GIF_SEARCH_STALE_TIME_MS = 5 * 60 * 1000;

/** The picker shows the trending shelf until the fan types a real query. */
export function isShowingTrendingShelf(fieldValue: string): boolean {
  return fieldValue.trim().length === 0;
}

/**
 * ISSUE-1051 R3 and R4. Whether the picker shows its no-results line in
 * place of the grid: only once a search the fan typed has settled with zero
 * GIFs. A new query is a new key with no answer yet, so `isPending` clears
 * the line the moment its request starts. The Trending shelf never shows the
 * line, because the fan did not make that search.
 */
export function isShowingGifNoResults(input: {
  /** The raw search field. */
  fieldValue: string;
  /** `search.isPending` from `useGifSearch`: true while the current query has no answer yet. */
  isPending: boolean;
  /** `readGifSearchResponse(...).isProviderAvailable`. */
  isProviderAvailable: boolean;
  /** `readGifSearchResponse(...).items.length`: the page the hook holds now. */
  pageItemCount: number;
  /** The accumulated grid's length (`items.length` in the sheet). */
  gridItemCount: number;
}): boolean {
  return (
    !isShowingTrendingShelf(input.fieldValue) &&
    !input.isPending &&
    input.isProviderAvailable &&
    input.pageItemCount === 0 &&
    input.gridItemCount === 0
  );
}

/** What to actually send to `/gif/search` for a given field state. */
export function resolveGifSearchQuery(fieldValue: string): string {
  const trimmed = fieldValue.trim();
  return trimmed.length === 0 ? GIF_TRENDING_QUERY : trimmed;
}

/**
 * Fold a freshly arrived page into the grid.
 *
 * Deduplicated on `providerGifId` because the provider pages by offset, so
 * a result set that shifts between two calls can hand back a GIF the grid
 * already shows. Two tiles with the same id are both a visible repeat and a
 * duplicate React key.
 */
export function appendGifPage(
  existing: typeGifResult[],
  page: typeGifResult[],
): typeGifResult[] {
  const seen = new Set(existing.map((item) => item.providerGifId));
  const added = page.filter((item) => {
    if (seen.has(item.providerGifId)) return false;
    seen.add(item.providerGifId);
    return true;
  });
  return added.length === 0 ? existing : [...existing, ...added];
}

export type typeGifSearchReading = {
  isProviderAvailable: boolean;
  items: typeGifResult[];
  nextCursor: string | null;
};

/**
 * Read one `/gif/search` response.
 *
 * `searchGifs.ts` already collapses every provider-side failure (non-2xx,
 * timeout, missing key, unknown provider name) into
 * `isProviderAvailable: false` with an empty list, so the flag is the only
 * signal this needs from a 200. An error envelope or a missing `data` block
 * reads unavailable too: the fan cannot search either way, and R4's frame is
 * a neutral informational state rather than an error dialog, so there is no
 * second failure look to route them to.
 */
export function readGifSearchResponse(
  response: typeGifSearchEnvelope | undefined,
  isTransportError: boolean,
): typeGifSearchReading {
  const unavailable: typeGifSearchReading = {
    isProviderAvailable: false,
    items: [],
    nextCursor: null,
  };
  if (isTransportError) return unavailable;
  if (!response || response.error || !response.data) return unavailable;
  const data = response.data;
  if (!data.isProviderAvailable) return unavailable;
  return {
    isProviderAvailable: true,
    items: data.items,
    nextCursor: data.nextCursor ?? null,
  };
}
